/**
 * YouTube Optimizer: the shapes stored on `VideoOptimization` JSON columns and
 * returned by the API. Browser-safe (types and constants only).
 *
 * AI assistants read a YouTube video's title and description, never its
 * transcript. A run turns what the video says into an AI-citable title, a
 * structured description and caption files. It surfaces what the video
 * already says and never adds claims.
 */

// ---------------------------------------------------------------------------
// Video types
// ---------------------------------------------------------------------------

export const VIDEO_TYPES = [
	"media-appearance",
	"talk",
	"product-demo",
	"panel",
	"other",
] as const;

export type VideoType = (typeof VIDEO_TYPES)[number];

export const VIDEO_TYPE_LABELS: Record<VideoType, string> = {
	"media-appearance": "Interview or media appearance",
	talk: "Talk or webinar",
	"product-demo": "Product demo or walkthrough",
	panel: "Panel discussion",
	other: "Other",
};

export function isVideoType(value: unknown): value is VideoType {
	return (
		typeof value === "string" &&
		(VIDEO_TYPES as readonly string[]).includes(value)
	);
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export const VIDEO_OPTIMIZATION_STATUSES = [
	"awaiting_audio",
	"queued",
	"transcribing",
	"correcting",
	"detecting",
	"generating",
	"checking",
	"completed",
	"regenerating",
	"needs_file",
	"failed",
	"expired",
] as const;

export type VideoOptimizationStatus =
	(typeof VIDEO_OPTIMIZATION_STATUSES)[number];

/** A run is working on these; it can't be deleted or started again. */
export const VIDEO_OPTIMIZATION_IN_FLIGHT_STATUSES = [
	"queued",
	"transcribing",
	"correcting",
	"detecting",
	"generating",
	"checking",
	"regenerating",
] as const satisfies readonly VideoOptimizationStatus[];

/** The steps of a first run, in order (regenerating is not one of them). */
export const VIDEO_OPTIMIZATION_RUN_STEPS = [
	"queued",
	"transcribing",
	"correcting",
	"detecting",
	"generating",
	"checking",
] as const satisfies readonly VideoOptimizationStatus[];

export function isVideoOptimizationInFlight(status: string): boolean {
	return (
		VIDEO_OPTIMIZATION_IN_FLIGHT_STATUSES as readonly string[]
	).includes(status);
}

/**
 * Where a run's audio came from. Every run takes its audio from the
 * browser-converted file; "link" only appears on rows made before the
 * server-side YouTube download was dropped (2026-10-18).
 */
export type VideoSourceKind = "link" | "file";

/** Files GET /video-optimizations/:id/download renders. */
export const VIDEO_OPTIMIZER_DOWNLOADS = [
	"srt",
	"vtt",
	"txt",
	"description",
] as const;
export type VideoOptimizerDownload = (typeof VIDEO_OPTIMIZER_DOWNLOADS)[number];

/** Where a run stopped, for `errorStage`. */
export type VideoOptimizationStage =
	| "upload"
	| "transcribe"
	| "correct"
	| "detect"
	| "generate"
	| "check"
	| "regenerate"
	| "sweep";

// ---------------------------------------------------------------------------
// Transcript
// ---------------------------------------------------------------------------

/**
 * One transcript word with its whisper-aligned time: `[text, startSec, endSec]`.
 * The text is gpt-transcribe's (punctuation kept on the word); only the TIMES
 * come from whisper-1.
 */
export type TimedWord = [text: string, start: number, end: number];

/** A sentence-started stretch of the transcript (chapters start at these). */
export interface TranscriptSegment {
	id: number;
	startSec: number;
	endSec: number;
	text: string;
}

/** heard → fixed, applied by code on top of the frozen words. */
export interface NameCorrection {
	id: string;
	heard: string;
	fixed: string;
	/** Times the fix was applied; null for a row the user just added. */
	count: number | null;
	enabled: boolean;
	source: "auto" | "user";
	reason?: string;
}

/** A correction the model proposed that the code refused, with why. */
export interface RejectedCorrection {
	heard: string;
	fixed: string;
	why: string;
}

/** How the transcript was made (shown in the run details, never secret). */
export interface TranscriptMeta {
	pieceCount: number;
	cutKinds: Record<string, number>;
	whisperParts: number;
	wordCount: number;
	alignedShare: number;
	keywords: string[];
	prompt: string;
	transcribeModel: string;
	timingModel: string;
	audioSec: number;
	wallMs: number;
	estimatedCostUsd: number;
	correctionsRejected?: RejectedCorrection[];
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

export interface VideoPerson {
	name: string;
	role: string;
	organization: string;
}

/** A person found in the transcript or the YouTube text, with evidence. */
export interface DetectedPerson extends VideoPerson {
	id: string;
	/** featured: guest/presenter/panelist; subject: someone the video is about. */
	relation: "featured" | "subject";
	quote: string | null;
	/** Where the evidence is, in seconds; null when it came from the metadata. */
	sec: number | null;
	origin: "detected" | "added";
}

// ---------------------------------------------------------------------------
// Outputs
// ---------------------------------------------------------------------------

export const TITLE_STRATEGIES = ["question", "finding", "format"] as const;
export type TitleStrategy = (typeof TITLE_STRATEGIES)[number];

export interface TitleOption {
	strategy: TitleStrategy;
	text: string;
	rationale: string;
}

export type PointsHeading =
	| "KEY TAKEAWAYS"
	| "WHAT YOU'LL LEARN"
	| "WHAT YOU'LL SEE";

export interface DescriptionChapter {
	/** Whole seconds; the first chapter is always 0. */
	sec: number;
	/** The transcript segment the chapter starts at. */
	segmentId: number;
	heading: string;
	summary: string;
}

export interface DescriptionLink {
	label: string;
	url?: string;
}

/**
 * Everything the description renderer needs. One skeleton for every video
 * type; optional blocks drop out: summary, (Presented by), (Panelists), a
 * points list whose heading follows the type, chapters, about, (Originally
 * aired on), (mentioned sources and tools), CONNECT.
 */
export interface DescriptionParts {
	videoType: VideoType;
	summary: string[];
	/** Talks: "<name>, <role> at <org>" (rendered "Presented by ….") */
	presentedBy: string | null;
	/** Panels: "<name>, <role> at <org>; …" (rendered "Panelists: ….") */
	panelists: string | null;
	pointsHeading: PointsHeading;
	points: string[];
	chapters: DescriptionChapter[];
	aboutHeading: string;
	about: string;
	/** Media appearances only (rendered "Originally aired on ….") */
	outlet: string | null;
	mentioned: DescriptionLink[];
	connect: Array<{ label: string; url: string }>;
}

/** Where a review item points in the output. */
export type ReviewTarget =
	| { kind: "title"; index: number }
	| { kind: "summary"; index: number }
	| { kind: "point"; index: number }
	| { kind: "chapter"; index: number }
	| { kind: "about" }
	| { kind: "description" };

export type ReviewItemKind = "unlisted-person" | "fact-check" | "check";

export interface ReviewItem {
	id: string;
	kind: ReviewItemKind;
	title: string;
	detail: string;
	/** Where in the video the evidence is; null when unknown. */
	sec: number | null;
	/** Human label for the part of the output, e.g. "Chapter 3". */
	whereLabel: string;
	target: ReviewTarget | null;
	/** The quoted output text the item is about, when there is one. */
	quote: string | null;
	/** Suggested replacement for `quote`, when the checker gave one. */
	suggestion: string | null;
	status: "open" | "accepted" | "dismissed";
}

/** A hand edit, re-applied after every regeneration while `before` appears. */
export interface ManualEdit {
	id: string;
	field: "description" | "title";
	/** For title edits: which title option. */
	titleIndex?: number;
	before: string;
	after: string;
	createdAt: string;
}

/** Contact links and sources/tools, one "Label: URL" per line. */
export interface VideoLinksBlock {
	contacts: string;
	sources: string;
}

/** The live YouTube text, read through the public Data API. */
export interface YouTubeVideoSnapshot {
	videoId: string;
	title: string;
	description: string;
	channelId: string;
	channelTitle: string;
	publishedAt: string | null;
	durationSec: number | null;
	fetchedAt: string;
}

export type PublishMatch = "exact" | "close" | "none";

export interface PublishComparison {
	match: PublishMatch;
	/** Which title option the live title matches best (null when none). */
	titleIndex: number | null;
	titleSimilarity: number;
	titleExact: boolean;
	descriptionSimilarity: number;
	descriptionExact: boolean;
}

/** One guard finding. */
export interface CheckIssue {
	kind: string;
	msg: string;
	fixable?: boolean;
}

/** One fact-check finding (luna, effort low). */
export interface FactCheckIssue {
	field: string;
	quote: string;
	kind:
		| "unsupported"
		| "misattributed"
		| "hedge-upgraded"
		| "changed-term"
		| "implies-outcome"
		| "outside-fact";
	severity: "error" | "minor";
	problem: string;
	fix: string;
}

export interface CheckPass {
	pass: boolean;
	hard: string[];
	soft: string[];
	factcheck: FactCheckIssue[];
	bytes: number;
}

/** What the checks found, before and after the one repair. */
export interface VideoOptimizationValidation {
	model: string;
	effort: string | null;
	before: CheckPass | null;
	after: CheckPass | null;
	repaired: boolean;
	fixes: string[];
	costs: Record<string, number>;
	/** Model output staged by the generate step for the check step. */
	pending?: PendingGeneration | null;
}

/** What the generate step hands the check step (kept off the outputs). */
export interface PendingGeneration {
	generated: GeneratedOutput;
	candidateQuestions: string[];
	generateCostUsd: number;
	generateMs: number;
	model: string;
	effort: string | null;
}

// ---------------------------------------------------------------------------
// Model output (what the generation model returns, before code renders it)
// ---------------------------------------------------------------------------

export interface GeneratedOutput {
	coreQuestion: string;
	titleOptions: TitleOption[];
	summaryParagraphs: string[];
	takeaways: Array<{ text: string; evidenceSegmentIds: string[] }>;
	chapters: Array<{ segmentId: string; heading: string; summary: string }>;
	about: string;
	candidateQuestionsUsed: Array<{
		question: string;
		usedIn: string;
		why: string;
	}>;
}
